const LOCK_NAME = 'builderpad-session-refresh';
const STORE_NAME = 'lease';
const LEASE_KEY = 'refresh';
const LEASE_MS = 30_000;
const WAIT_MS = 35_000;
const STORAGE_TIMEOUT_MS = 2_000;
const POLL_MS = 100;

export class SessionRefreshLockUnavailableError extends Error {
  constructor() {
    super('Session refresh coordination is unavailable. Please retry.');
    this.name = 'SessionRefreshLockUnavailableError';
  }
}

type Lease = { owner: string; expiresAt: number };
type LeaseOperation = 'acquire' | 'owned' | 'release';
export type SessionRefreshOwnership = (action?: () => void) => Promise<boolean>;

const openDatabase = (): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    let settled = false;
    const fail = () => {
      if (!settled) {
        settled = true;
        clearTimeout(timer);
        reject(new SessionRefreshLockUnavailableError());
      }
    };
    const timer = setTimeout(fail, STORAGE_TIMEOUT_MS);
    try {
      const request = indexedDB.open(LOCK_NAME, 1);
      request.onupgradeneeded = () => request.result.createObjectStore(STORE_NAME);
      request.onerror = fail;
      request.onblocked = fail;
      request.onsuccess = () => {
        const database = request.result;
        if (settled) {
          database.close();
          return;
        }
        settled = true;
        clearTimeout(timer);
        database.onversionchange = () => database.close();
        resolve(database);
      };
    } catch {
      fail();
    }
  });

// Acquisition, ownership checks and conditional release serialize on one shared record.
// A request's success is not a commit: only transaction completion may let a refresh start.
const leaseOperation = (
  database: IDBDatabase,
  owner: string,
  operation: LeaseOperation,
  action?: () => void
): Promise<boolean> =>
  new Promise((resolve, reject) => {
    let transaction: IDBTransaction;
    let settled = false;
    let result = false;
    let expiresAt = 0;
    let actionError: unknown;
    const fail = () => {
      if (!settled) {
        settled = true;
        clearTimeout(timer);
        reject(actionError ?? new SessionRefreshLockUnavailableError());
      }
    };
    const timer = setTimeout(() => {
      try {
        transaction?.abort();
      } catch {
        // A completed/aborted transaction cannot be aborted again.
      }
      fail();
    }, STORAGE_TIMEOUT_MS);
    try {
      transaction = database.transaction(STORE_NAME, 'readwrite');
      transaction.onabort = fail;
      transaction.onerror = fail;
      transaction.oncomplete = () => {
        if (!settled) {
          settled = true;
          clearTimeout(timer);
          resolve(result && (action ? true : expiresAt > Date.now()));
        }
      };
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(LEASE_KEY);
      request.onsuccess = () => {
        const lease: Lease | undefined = request.result;
        if (operation === 'acquire') {
          if (!lease || lease.expiresAt <= Date.now()) {
            expiresAt = Date.now() + LEASE_MS;
            store.put({ owner, expiresAt }, LEASE_KEY);
            result = true;
          }
        } else if (operation === 'owned') {
          // An expired owner is fenced even before another tab takes the lease.
          expiresAt = lease?.expiresAt ?? 0;
          result = lease?.owner === owner && lease.expiresAt > Date.now();
          if (result && action) {
            // Commit decisions execute while the readwrite transaction excludes a successor.
            // Awaiting only a Boolean would leave a suspension window before credential writes.
            try {
              action();
            } catch (error) {
              actionError = error;
              transaction.abort();
              fail();
            }
          }
        } else if (lease?.owner === owner) {
          store.delete(LEASE_KEY);
        }
      };
    } catch {
      fail();
    }
  });

async function withIndexedDBLease<T>(run: (stillOwner: SessionRefreshOwnership) => Promise<T>): Promise<T> {
  const deadline = performance.now() + WAIT_MS;
  const owner = crypto.randomUUID();
  const database = await openDatabase();
  try {
    while (performance.now() < deadline) {
      if (await leaseOperation(database, owner, 'acquire')) {
        try {
          return await run((action) => leaseOperation(database, owner, 'owned', action));
        } finally {
          // Expiry recovers a failed release. Never delete a successor's lease.
          await leaseOperation(database, owner, 'release').catch(() => undefined);
        }
      }
      await new Promise((resolve) => setTimeout(resolve, POLL_MS));
    }
    throw new SessionRefreshLockUnavailableError();
  } finally {
    database.close();
  }
}

export async function withSessionRefreshLock<T>(run: (stillOwner: SessionRefreshOwnership) => Promise<T>): Promise<T> {
  if (!navigator.locks) {
    return withIndexedDBLease(run);
  }
  const controller = new AbortController();
  let started = false;
  const timer = setTimeout(() => controller.abort(), WAIT_MS);
  try {
    return await navigator.locks.request(LOCK_NAME, { mode: 'exclusive', signal: controller.signal }, async () => {
      started = true;
      clearTimeout(timer);
      return run(async (action) => {
        action?.();
        return true;
      });
    });
  } catch (error) {
    // Once entered, preserve the caller's error. A rejected lock must not switch protocols.
    if (started) {
      throw error;
    }
    throw new SessionRefreshLockUnavailableError();
  } finally {
    clearTimeout(timer);
  }
}
